<template>
  <v-app :style="{background: $vuetify.theme.themes[theme].background}">
    <div style="display:flex; flex-direction:column; flex:1;">
      <TheNavBar
        v-if="authenticated || isLoginCompletePage"
        :hasOnlyOneAccount="hasOnlyOneAccount"
        :isFocusMode="isLockedPage"
        :isAccountWizard="isAccountWizard"
        @logout="logout"
      />
      <TheMainSidebar v-if="!isLockedPage && (authenticated || isLoginCompletePage) && !hasOnlyOneAccount" />
      <AccountSidebar v-if="authenticated && isAccountPage" />
      <AdminSidebar v-if="isSuperAdmin && isAdminPage" />

      <v-main :style="`padding-left: ${mainPaddingLeft}px !important;`">
        <v-fade-transition mode="out-in">
          <div>
            <TheSkeletonLoader
              v-if="loading && (authenticated || isLoginCompletePage)"
              key="loading"
            />
          </div>
        </v-fade-transition>

        <transition
          name="fade"
          mode="out-in"
        >
          <router-view
            v-if="authenticated || $route.meta.isForLoggedOutUsers"
            key="data"
          />
        </transition>
      </v-main>
    </div>
  </v-app>
</template>

<script>
import { MOBILE_MAX_WIDTH } from "@/constants";
import { mapGetters } from 'vuex';

import TheNavBar from "@/components/TheNavBar";
import TheMainSidebar from "@/components/TheMainSidebar";
import TheSkeletonLoader from "@/components/TheSkeletonLoader.vue";
import AccountSidebar from "@/components/AccountSidebar.vue";
import AdminSidebar from "@/components/AdminSidebar.vue";


export default {
  name: "app",
  components: {
    TheNavBar,
    TheMainSidebar,
    TheSkeletonLoader,
    AccountSidebar,
    AdminSidebar,
  },
  data() {
    return {
      sidebarFull: true,
      sidebarFullWidth: 250,
      sidebarMinWidth: 0,
    };
  },
  computed: {
    ...mapGetters({
      authenticated: "auth/isAuthenticated",
      loading: 'app/getDataLoading',
      fullyLoaded: 'app/getDataLoaded',
      accounts: "accounts/getAccounts",
      isMainSidebarOpen: "app/getMainSidebarOpenState",
      isSecondSidebarOpen: "app/getSecondSidebarOpenState",
      isSuperAdmin: "auth/isSuperAdmin",
    }),
    hasOnlyOneAccount() {
      return this.accounts.length === 1;
    },
    isAccountPage() {
      return this.$route.path.toLowerCase().includes('/account/');
    },
    isAdminPage() {
      return this.$route.path.toLowerCase().includes('/super/');
    },
    hasSecondSidebar() {
      return this.isAccountPage || this.isAdminPage;
    },
    isLockedPage() {
      return this.$route.path.toLowerCase().includes('/newaccountwizard');
    },
    isAccountWizard() {
      return this.$route.path.toLowerCase().includes('/newaccountwizard');
    },
    theme() {
      return this.$vuetify.theme.dark ? "dark" : "light";
    },
    mainPaddingLeft() {
      if (this.isLockedPage || !this.authenticated ||
        (!this.hasSecondSidebar && this.$vuetify.breakpoint.width <= MOBILE_MAX_WIDTH) ||
        (!this.hasSecondSidebar && !this.isMainSidebarOpen)
      ) {
        return '0';
      }

      if (this.hasSecondSidebar && this.$vuetify.breakpoint.width <= MOBILE_MAX_WIDTH) {
        return '56';
      }

      if (!this.hasSecondSidebar && this.isMainSidebarOpen) {
        return '325';
      }

      if (this.isMainSidebarOpen && this.isSecondSidebarOpen) {
        return '650';
      }

      if (this.isMainSidebarOpen && !this.isSecondSidebarOpen) {
        return '381';
      }

      if (!this.isMainSidebarOpen && this.isSecondSidebarOpen) {
        return '325';
      }

      if (!this.isMainSidebarOpen && !this.isSecondSidebarOpen) {
        return '56';
      }
    },
    isLoginCompletePage() {
      return this.$route.path.includes('/login/complete');
    }
  },
  watch: {
    authenticated(newValue) {
      this.$store.dispatch('app/resetLoading');
    },
    $route(newValue, oldValue) {
      if (newValue.path !== oldValue.path) {
        this.$store.dispatch('app/resetLoading');
        this.$msal.extendSession(); // Silently acquire new access token when navigating
      }
      if (this.hasOnlyOneAccount) {
        this.$store.dispatch("app/setMainSidebarOpenState", false);
      }
    },
  },
  async created() {
    this.$broadcast.channel.addEventListener('message', (event) => {
      if (event.data.type === 'logout') {
        this.logout();
      }
    });

    // Consumed here (App only ever mounts once per real page load) rather than in
    // NewAccountWizard's own mounted(), which can run more than once while the router resolves
    // its very first navigation on a cold boot - reading sessionStorage there let whichever
    // instance mounted first consume the flag, sometimes leaving the surviving instance with
    // nothing to react to.
    if (sessionStorage.getItem("justSignedUp")) {
      sessionStorage.removeItem("justSignedUp");
      this.$store.dispatch("app/setShowWelcomePopup", true);
    }

    // Handle initial state
    this.$store.dispatch('app/resetLoading');
    if (this.hasOnlyOneAccount) {
      this.$store.dispatch("app/setMainSidebarOpenState", false);
    }
  },
  methods: {
    logout() {
      console.log('request logout');
      this.$msal.logout();
    },
  },
};
</script>

<style lang="scss">
$duration: 0.3s;

.fade {
  &-enter-active,
  &-leave-active {
    transition-duration: $duration;
    transition-property: opacity;
    transition-timing-function: ease;
  }

  &-enter-active {
    transition-delay: $duration;
  }

  &-enter,
  &-leave-active {
    opacity: 0;
  }
}

.loading {
  position: relative;
  height: calc(100vh - 60px);
  overflow: hidden;
}
</style>

